import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  BarChart3,
  Building2,
  Users,
  Search,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  Download,
  ArrowRight,
  Calendar,
  ChevronDown,
  ChevronRight,
  Layers,
  FileText,
  RefreshCw,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { fetchFilieresGrouped, getSemestersByFiliere } from '@/services/filieresService';
import { getAvailableCampuses, getGroupesByFiliere } from '@/services/groupsService';
import { fetchAcademicYears, AcademicYear } from '@/services/academicPlanningService';
import { scheduleService } from '../services/scheduleService';
import type { FiliereWithOptions, ModuleElement, SemesterWithModules } from '@/types/filiere.types';

interface Campus {
  id: string;
  name: string;
  sigle: string | null;
  isMainCampus: boolean;
}

type ScheduleStatus = 'NOT_STARTED' | 'PARTIAL' | 'COMPLETE';
type ViewMode = 'overview' | 'detailed';

interface ModuleProgress {
  moduleElementId: string;
  moduleName: string;
  volumeHoraire: number;
  hoursScheduled: number;
  status: ScheduleStatus;
  percentage: number;
}

interface GroupProgress {
  groupeId: string;
  groupeName: string;
  niveau: number;
  totalModules: number;
  modulesComplete: number;
  modulesPartial: number;
  modulesNotStarted: number;
  totalHoursRequired: number;
  totalHoursScheduled: number;
  percentage: number;
  status: ScheduleStatus;
}

interface NiveauProgress {
  niveau: number;
  groups: GroupProgress[];
  totalModules: number;
  modulesComplete: number;
  modulesPartial: number;
  modulesNotStarted: number;
  percentage: number;
  status: ScheduleStatus;
}

interface FiliereProgress {
  filiereId: string;
  filiereName: string;
  codeFiliere: string;
  niveaux: NiveauProgress[];
  totalGroups: number;
  groupsComplete: number;
  groupsPartial: number;
  groupsNotStarted: number;
  percentage: number;
  status: ScheduleStatus;
}

interface OverallSummary {
  totalFilieres: number;
  totalGroups: number;
  groupsComplete: number;
  groupsPartial: number;
  groupsNotStarted: number;
  overallPercentage: number;
}

export const SchedulingProgressPage: React.FC = () => {
  const navigate = useNavigate();
  const { tokens } = useAuthStore();

  // Loading & error states
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState<AcademicYear | null>(null);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [hasMultipleCampuses, setHasMultipleCampuses] = useState(false);
  const [selectedCampusId, setSelectedCampusId] = useState<string | null>(null);
  const [filieres, setFilieres] = useState<FiliereWithOptions[]>([]);
  const [selectedSemester, setSelectedSemester] = useState<1 | 2>(1);

  // Progress data
  const [filieresProgress, setFilieresProgress] = useState<FiliereProgress[]>([]);
  const [loadingProgress, setLoadingProgress] = useState(false);

  // View state
  const [, ] = useState<ViewMode>('overview');
  const [expandedFilieres, setExpandedFilieres] = useState<Set<string>>(new Set());
  const [expandedNiveaux, setExpandedNiveaux] = useState<Set<string>>(new Set());
  const [selectedGroupeId, setSelectedGroupeId] = useState<string | null>(null);
  const [groupModuleProgress, setGroupModuleProgress] = useState<ModuleProgress[]>([]);
  const [loadingGroupDetails, setLoadingGroupDetails] = useState(false);

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  // Load initial data
  const loadInitialData = useCallback(async () => {
    if (!tokens?.accessToken) return;

    setIsLoading(true);
    setError(null);

    try {
      const [yearsData, campusData, filieresData] = await Promise.all([
        fetchAcademicYears(tokens.accessToken),
        getAvailableCampuses(),
        fetchFilieresGrouped(true),
      ]);

      setAcademicYears(yearsData);
      setCampuses(campusData.campuses);
      setHasMultipleCampuses(campusData.hasMultipleCampuses);
      setFilieres(filieresData);

      // Set current academic year
      const currentYear = yearsData.find((y: AcademicYear) => y.isCurrent);
      if (currentYear) {
        setSelectedAcademicYear(currentYear);
      }

      // Set default campus
      if (campusData.hasMultipleCampuses && campusData.campuses.length > 0) {
        const mainCampus = campusData.campuses.find((c: Campus) => c.isMainCampus);
        setSelectedCampusId(mainCampus?.id || campusData.campuses[0].id);
      } else if (campusData.campuses.length === 1) {
        setSelectedCampusId(campusData.campuses[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  }, [tokens?.accessToken]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Load progress data for all filieres
  const loadProgressData = useCallback(async () => {
    if (!selectedAcademicYear || !filieres.length) return;

    setLoadingProgress(true);

    try {
      const progressPromises = filieres.map(async (filiere) => {
        // Get groups for this filiere
        const groups = await getGroupesByFiliere(
          filiere.id,
          selectedAcademicYear.id,
          selectedCampusId || undefined
        );

        // Get semesters for this filiere
        let semesters: SemesterWithModules[] = [];
        try {
          semesters = await getSemestersByFiliere(filiere.id);
        } catch {
          // No semesters configured
        }

        // Group progress by niveau
        const niveauxMap = new Map<number, GroupProgress[]>();

        for (const groupe of groups) {
          // Calculate expected semester number
          const semesterNumber = (groupe.niveau - 1) * 2 + selectedSemester;
          const semester = semesters.find(s => s.number === semesterNumber);

          if (!semester) {
            // No semester data, group is not started
            const groupProgress: GroupProgress = {
              groupeId: groupe.id,
              groupeName: groupe.name,
              niveau: groupe.niveau,
              totalModules: 0,
              modulesComplete: 0,
              modulesPartial: 0,
              modulesNotStarted: 0,
              totalHoursRequired: 0,
              totalHoursScheduled: 0,
              percentage: 0,
              status: 'NOT_STARTED',
            };

            const niveauGroups = niveauxMap.get(groupe.niveau) || [];
            niveauGroups.push(groupProgress);
            niveauxMap.set(groupe.niveau, niveauGroups);
            continue;
          }

          // Get all module elements for this semester
          const matieres: ModuleElement[] = [];
          semester.modules.forEach(module => {
            matieres.push(...module.elements);
          });

          // Get schedule entries for this group
          let entries: any[] = [];
          try {
            entries = await scheduleService.getEntries({
              academicYearId: selectedAcademicYear.id,
              groupeId: groupe.id,
            });
          } catch {
            // No entries
          }

          // Calculate scheduled hours per matière
          const hoursMap = new Map<string, number>();
          entries.forEach(entry => {
            const sessions = entry.sessions || [];
            const activeSessions = sessions.filter((s: any) => !s.isCancelled);
            const totalMinutes = activeSessions.reduce((sum: number, session: any) => {
              const [startH, startM] = session.startTime.split(':').map(Number);
              const [endH, endM] = session.endTime.split(':').map(Number);
              return sum + ((endH * 60 + endM) - (startH * 60 + startM));
            }, 0);
            const hours = totalMinutes / 60;

            const currentHours = hoursMap.get(entry.moduleElementId) || 0;
            hoursMap.set(entry.moduleElementId, currentHours + hours);
          });

          // Calculate progress
          let totalHoursRequired = 0;
          let totalHoursScheduled = 0;
          let modulesComplete = 0;
          let modulesPartial = 0;
          let modulesNotStarted = 0;

          matieres.forEach(matiere => {
            const volumeHoraire = matiere.volumeHoraire || 0;
            const scheduled = hoursMap.get(matiere.id) || 0;

            totalHoursRequired += volumeHoraire;
            totalHoursScheduled += scheduled;

            if (volumeHoraire > 0 && scheduled >= volumeHoraire) {
              modulesComplete++;
            } else if (scheduled > 0) {
              modulesPartial++;
            } else {
              modulesNotStarted++;
            }
          });

          const percentage = totalHoursRequired > 0
            ? Math.min(100, (totalHoursScheduled / totalHoursRequired) * 100)
            : 0;

          let status: ScheduleStatus = 'NOT_STARTED';
          if (modulesComplete === matieres.length && matieres.length > 0) {
            status = 'COMPLETE';
          } else if (modulesComplete > 0 || modulesPartial > 0) {
            status = 'PARTIAL';
          }

          const groupProgress: GroupProgress = {
            groupeId: groupe.id,
            groupeName: groupe.name,
            niveau: groupe.niveau,
            totalModules: matieres.length,
            modulesComplete,
            modulesPartial,
            modulesNotStarted,
            totalHoursRequired,
            totalHoursScheduled,
            percentage,
            status,
          };

          const niveauGroups = niveauxMap.get(groupe.niveau) || [];
          niveauGroups.push(groupProgress);
          niveauxMap.set(groupe.niveau, niveauGroups);
        }

        // Build niveau progress
        const niveauxProgress: NiveauProgress[] = [];
        for (let niveau = 1; niveau <= filiere.nombreAnnees; niveau++) {
          const groups = niveauxMap.get(niveau) || [];
          const totalModules = groups.reduce((sum, g) => sum + g.totalModules, 0);
          const modulesComplete = groups.reduce((sum, g) => sum + g.modulesComplete, 0);
          const modulesPartial = groups.reduce((sum, g) => sum + g.modulesPartial, 0);
          const modulesNotStarted = groups.reduce((sum, g) => sum + g.modulesNotStarted, 0);

          const totalRequired = groups.reduce((sum, g) => sum + g.totalHoursRequired, 0);
          const totalScheduled = groups.reduce((sum, g) => sum + g.totalHoursScheduled, 0);
          const percentage = totalRequired > 0 ? (totalScheduled / totalRequired) * 100 : 0;

          const completeGroups = groups.filter(g => g.status === 'COMPLETE').length;
          let status: ScheduleStatus = 'NOT_STARTED';
          if (completeGroups === groups.length && groups.length > 0) {
            status = 'COMPLETE';
          } else if (completeGroups > 0 || groups.some(g => g.status === 'PARTIAL')) {
            status = 'PARTIAL';
          }

          niveauxProgress.push({
            niveau,
            groups,
            totalModules,
            modulesComplete,
            modulesPartial,
            modulesNotStarted,
            percentage,
            status,
          });
        }

        // Build filiere progress
        const totalGroups = groups.length;
        const groupsComplete = niveauxProgress.reduce(
          (sum, n) => sum + n.groups.filter(g => g.status === 'COMPLETE').length,
          0
        );
        const groupsPartial = niveauxProgress.reduce(
          (sum, n) => sum + n.groups.filter(g => g.status === 'PARTIAL').length,
          0
        );
        const groupsNotStarted = niveauxProgress.reduce(
          (sum, n) => sum + n.groups.filter(g => g.status === 'NOT_STARTED').length,
          0
        );

        const totalRequired = niveauxProgress.reduce(
          (sum, n) => sum + n.groups.reduce((s, g) => s + g.totalHoursRequired, 0),
          0
        );
        const totalScheduled = niveauxProgress.reduce(
          (sum, n) => sum + n.groups.reduce((s, g) => s + g.totalHoursScheduled, 0),
          0
        );
        const percentage = totalRequired > 0 ? (totalScheduled / totalRequired) * 100 : 0;

        let status: ScheduleStatus = 'NOT_STARTED';
        if (groupsComplete === totalGroups && totalGroups > 0) {
          status = 'COMPLETE';
        } else if (groupsComplete > 0 || groupsPartial > 0) {
          status = 'PARTIAL';
        }

        return {
          filiereId: filiere.id,
          filiereName: filiere.filiere,
          codeFiliere: filiere.codeFiliere,
          niveaux: niveauxProgress,
          totalGroups,
          groupsComplete,
          groupsPartial,
          groupsNotStarted,
          percentage,
          status,
        };
      });

      const results = await Promise.all(progressPromises);
      setFilieresProgress(results);
    } catch (err) {
      console.error('Error loading progress:', err);
      setError('Erreur lors du chargement de la progression');
    } finally {
      setLoadingProgress(false);
    }
  }, [selectedAcademicYear, selectedCampusId, filieres, selectedSemester]);

  useEffect(() => {
    if (selectedAcademicYear && filieres.length > 0) {
      loadProgressData();
    }
  }, [loadProgressData]);

  // Load detailed module progress for selected group
  const loadGroupDetails = useCallback(async (groupeId: string, filiereId: string, niveau: number) => {
    if (!selectedAcademicYear) return;

    setLoadingGroupDetails(true);
    setSelectedGroupeId(groupeId);

    try {
      const semesters = await getSemestersByFiliere(filiereId);
      const semesterNumber = (niveau - 1) * 2 + selectedSemester;
      const semester = semesters.find(s => s.number === semesterNumber);

      if (!semester) {
        setGroupModuleProgress([]);
        return;
      }

      const matieres: ModuleElement[] = [];
      semester.modules.forEach(module => {
        matieres.push(...module.elements);
      });

      const entries = await scheduleService.getEntries({
        academicYearId: selectedAcademicYear.id,
        groupeId,
      });

      const hoursMap = new Map<string, number>();
      entries.forEach(entry => {
        const sessions = entry.sessions || [];
        const activeSessions = sessions.filter((s: any) => !s.isCancelled);
        const totalMinutes = activeSessions.reduce((sum: number, session: any) => {
          const [startH, startM] = session.startTime.split(':').map(Number);
          const [endH, endM] = session.endTime.split(':').map(Number);
          return sum + ((endH * 60 + endM) - (startH * 60 + startM));
        }, 0);
        const hours = totalMinutes / 60;
        const currentHours = hoursMap.get(entry.moduleElementId) || 0;
        hoursMap.set(entry.moduleElementId, currentHours + hours);
      });

      const progress: ModuleProgress[] = matieres.map(matiere => {
        const volumeHoraire = matiere.volumeHoraire || 0;
        const hoursScheduled = hoursMap.get(matiere.id) || 0;
        let status: ScheduleStatus = 'NOT_STARTED';
        let percentage = 0;

        if (volumeHoraire > 0) {
          percentage = Math.min(100, (hoursScheduled / volumeHoraire) * 100);
          if (hoursScheduled >= volumeHoraire) {
            status = 'COMPLETE';
          } else if (hoursScheduled > 0) {
            status = 'PARTIAL';
          }
        } else if (hoursScheduled > 0) {
          status = 'PARTIAL';
          percentage = 50;
        }

        return {
          moduleElementId: matiere.id,
          moduleName: matiere.name,
          volumeHoraire,
          hoursScheduled,
          status,
          percentage,
        };
      });

      setGroupModuleProgress(progress);
    } catch (err) {
      console.error('Error loading group details:', err);
    } finally {
      setLoadingGroupDetails(false);
    }
  }, [selectedAcademicYear, selectedSemester]);

  // Overall summary
  const overallSummary = useMemo((): OverallSummary => {
    const totalFilieres = filieresProgress.length;
    const totalGroups = filieresProgress.reduce((sum, f) => sum + f.totalGroups, 0);
    const groupsComplete = filieresProgress.reduce((sum, f) => sum + f.groupsComplete, 0);
    const groupsPartial = filieresProgress.reduce((sum, f) => sum + f.groupsPartial, 0);
    const groupsNotStarted = filieresProgress.reduce((sum, f) => sum + f.groupsNotStarted, 0);

    const totalRequired = filieresProgress.reduce(
      (sum, f) => sum + f.niveaux.reduce((s, n) => s + n.groups.reduce((g, gr) => g + gr.totalHoursRequired, 0), 0),
      0
    );
    const totalScheduled = filieresProgress.reduce(
      (sum, f) => sum + f.niveaux.reduce((s, n) => s + n.groups.reduce((g, gr) => g + gr.totalHoursScheduled, 0), 0),
      0
    );

    const overallPercentage = totalRequired > 0 ? (totalScheduled / totalRequired) * 100 : 0;

    return {
      totalFilieres,
      totalGroups,
      groupsComplete,
      groupsPartial,
      groupsNotStarted,
      overallPercentage,
    };
  }, [filieresProgress]);

  // Filter filieres by search
  const filteredFilieres = useMemo(() => {
    if (!searchQuery) return filieresProgress;
    const query = searchQuery.toLowerCase();
    return filieresProgress.filter(
      f => f.filiereName.toLowerCase().includes(query) || f.codeFiliere.toLowerCase().includes(query)
    );
  }, [filieresProgress, searchQuery]);

  const toggleFiliere = (filiereId: string) => {
    const newExpanded = new Set(expandedFilieres);
    if (newExpanded.has(filiereId)) {
      newExpanded.delete(filiereId);
    } else {
      newExpanded.add(filiereId);
    }
    setExpandedFilieres(newExpanded);
  };

  const toggleNiveau = (filiereId: string, niveau: number) => {
    const key = `${filiereId}-${niveau}`;
    const newExpanded = new Set(expandedNiveaux);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedNiveaux(newExpanded);
  };

  const goToSchedule = (groupeId?: string, filiereId?: string, niveau?: number) => {
    const params = new URLSearchParams();
    if (filiereId) params.set('filiere', filiereId);
    if (niveau) params.set('niveau', String(niveau));
    if (groupeId) params.set('groupe', groupeId);
    navigate(`/dg/calendrier/emplois-du-temps?${params.toString()}`);
  };

  const getStatusIcon = (status: ScheduleStatus, size: 'sm' | 'md' = 'md') => {
    const sizeClass = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
    switch (status) {
      case 'COMPLETE':
        return <CheckCircle className={`${sizeClass} text-green-500`} />;
      case 'PARTIAL':
        return <Clock className={`${sizeClass} text-amber-500`} />;
      case 'NOT_STARTED':
        return <AlertCircle className={`${sizeClass} text-gray-400`} />;
    }
  };

  const getStatusBadge = (status: ScheduleStatus) => {
    switch (status) {
      case 'COMPLETE':
        return <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">Complet</span>;
      case 'PARTIAL':
        return <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">Partiel</span>;
      case 'NOT_STARTED':
        return <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">Non planifié</span>;
    }
  };

  const getProgressBarColor = (status: ScheduleStatus) => {
    switch (status) {
      case 'COMPLETE':
        return 'bg-green-500';
      case 'PARTIAL':
        return 'bg-amber-500';
      case 'NOT_STARTED':
        return 'bg-gray-300';
    }
  };

  const getNiveauLabel = (niveau: number): string => {
    const suffixes: Record<number, string> = {
      1: '1ère Année',
      2: '2ème Année',
      3: '3ème Année',
      4: '4ème Année',
      5: '5ème Année',
    };
    return suffixes[niveau] || `${niveau}ème Année`;
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Filière', 'Code', 'Niveau', 'Groupe', 'Modules', 'Complets', 'Partiels', 'Non planifiés', 'Progression'];
    const rows: (string | number)[][] = [];

    filieresProgress.forEach(filiere => {
      filiere.niveaux.forEach(niveau => {
        niveau.groups.forEach(group => {
          rows.push([
            filiere.filiereName,
            filiere.codeFiliere,
            getNiveauLabel(niveau.niveau),
            group.groupeName,
            group.totalModules,
            group.modulesComplete,
            group.modulesPartial,
            group.modulesNotStarted,
            `${Math.round(group.percentage)}%`,
          ]);
        });
      });
    });

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `progression_planification_S${selectedSemester}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-50 rounded-xl">
              <BarChart3 className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                Rapport de Progression - Planification
              </h1>
              <p className="text-sm text-gray-500">
                Suivi de l'avancement par filière, niveau et groupe
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => loadProgressData()}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Rafraîchir"
            >
              <RefreshCw className={`h-5 w-5 ${loadingProgress ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={exportToCSV}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Exporter CSV
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Academic Year */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <select
              value={selectedAcademicYear?.id || ''}
              onChange={(e) => {
                const year = academicYears.find(y => y.id === e.target.value);
                setSelectedAcademicYear(year || null);
              }}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {academicYears.map((year) => (
                <option key={year.id} value={year.id}>
                  {year.name} {year.isCurrent ? '(Courante)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Campus */}
          {hasMultipleCampuses && campuses.length > 1 && (
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-gray-400" />
              <select
                value={selectedCampusId || ''}
                onChange={(e) => setSelectedCampusId(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {campuses.map((campus) => (
                  <option key={campus.id} value={campus.id}>
                    {campus.sigle || campus.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Semester */}
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setSelectedSemester(1)}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                selectedSemester === 1
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Semestre 1
            </button>
            <button
              onClick={() => setSelectedSemester(2)}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                selectedSemester === 2
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Semestre 2
            </button>
          </div>

          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une filière..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex">
        {loadingProgress ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : (
          <>
            {/* Main Content */}
            <div className="flex-1 overflow-auto p-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-5 gap-4 mb-6">
                {/* Overall Progress */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-600">Progression globale</span>
                    <span className="text-lg font-bold text-primary-700">
                      {Math.round(overallSummary.overallPercentage)}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-600 rounded-full transition-all duration-300"
                      style={{ width: `${overallSummary.overallPercentage}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {overallSummary.totalFilieres} filières, {overallSummary.totalGroups} groupes
                  </p>
                </div>

                {/* Complete */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{overallSummary.groupsComplete}</p>
                      <p className="text-sm text-gray-500">Complets</p>
                    </div>
                  </div>
                </div>

                {/* Partial */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{overallSummary.groupsPartial}</p>
                      <p className="text-sm text-gray-500">Partiels</p>
                    </div>
                  </div>
                </div>

                {/* Not Started */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <AlertCircle className="w-5 h-5 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{overallSummary.groupsNotStarted}</p>
                      <p className="text-sm text-gray-500">Non planifiés</p>
                    </div>
                  </div>
                </div>

                {/* Total Groups */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{overallSummary.totalGroups}</p>
                      <p className="text-sm text-gray-500">Total groupes</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Filieres List */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Détail par filière ({filteredFilieres.length})
                  </h3>
                </div>

                <div className="divide-y divide-gray-100">
                  {filteredFilieres.map((filiere) => (
                    <div key={filiere.filiereId}>
                      {/* Filière Row */}
                      <button
                        onClick={() => toggleFiliere(filiere.filiereId)}
                        className="w-full px-4 py-3 flex items-center gap-4 hover:bg-gray-50 transition-colors"
                      >
                        {expandedFilieres.has(filiere.filiereId) ? (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        )}
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="p-2 bg-primary-50 rounded-lg">
                            <Layers className="w-4 h-4 text-primary-600" />
                          </div>
                          <div className="flex-1 min-w-0 text-left">
                            <p className="text-sm font-medium text-gray-900 leading-snug break-words" title={filiere.filiereName}>
                              {filiere.filiereName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {filiere.codeFiliere} - {filiere.totalGroups} groupe{filiere.totalGroups > 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-32">
                            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all duration-300 ${getProgressBarColor(filiere.status)}`}
                                style={{ width: `${filiere.percentage}%` }}
                              />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{Math.round(filiere.percentage)}%</p>
                          </div>
                          {getStatusBadge(filiere.status)}
                        </div>
                      </button>

                      {/* Niveaux */}
                      {expandedFilieres.has(filiere.filiereId) && (
                        <div className="bg-gray-50 border-t border-gray-100">
                          {filiere.niveaux.map((niveau) => (
                            <div key={niveau.niveau}>
                              {/* Niveau Row */}
                              <button
                                onClick={() => toggleNiveau(filiere.filiereId, niveau.niveau)}
                                className="w-full pl-12 pr-4 py-2.5 flex items-center gap-4 hover:bg-gray-100 transition-colors"
                              >
                                {expandedNiveaux.has(`${filiere.filiereId}-${niveau.niveau}`) ? (
                                  <ChevronDown className="w-4 h-4 text-gray-400" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-gray-400" />
                                )}
                                <div className="flex-1 min-w-0 text-left">
                                  <p className="text-sm font-medium text-gray-700">
                                    {getNiveauLabel(niveau.niveau)}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {niveau.groups.length} groupe{niveau.groups.length > 1 ? 's' : ''}
                                  </p>
                                </div>
                                <div className="flex items-center gap-4">
                                  <div className="w-24">
                                    <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                      <div
                                        className={`h-full rounded-full transition-all duration-300 ${getProgressBarColor(niveau.status)}`}
                                        style={{ width: `${niveau.percentage}%` }}
                                      />
                                    </div>
                                  </div>
                                  <span className="text-xs text-gray-500 w-10">{Math.round(niveau.percentage)}%</span>
                                  {getStatusIcon(niveau.status, 'sm')}
                                </div>
                              </button>

                              {/* Groups */}
                              {expandedNiveaux.has(`${filiere.filiereId}-${niveau.niveau}`) && (
                                <div className="bg-white border-t border-gray-100">
                                  {niveau.groups.map((group) => (
                                    <div
                                      key={group.groupeId}
                                      className={`pl-20 pr-4 py-2 flex items-center gap-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                                        selectedGroupeId === group.groupeId ? 'bg-primary-50' : ''
                                      }`}
                                      onClick={() => loadGroupDetails(group.groupeId, filiere.filiereId, niveau.niveau)}
                                    >
                                      <Users className="w-4 h-4 text-gray-400" />
                                      <div className="flex-1 min-w-0 text-left">
                                        <p className="text-sm text-gray-900">{group.groupeName}</p>
                                        <p className="text-xs text-gray-500">
                                          {group.totalModules} matières - {group.modulesComplete} complets
                                        </p>
                                      </div>
                                      <div className="flex items-center gap-4">
                                        <div className="w-20">
                                          <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                            <div
                                              className={`h-full rounded-full transition-all duration-300 ${getProgressBarColor(group.status)}`}
                                              style={{ width: `${group.percentage}%` }}
                                            />
                                          </div>
                                        </div>
                                        <span className="text-xs text-gray-500 w-10">{Math.round(group.percentage)}%</span>
                                        {getStatusIcon(group.status, 'sm')}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            goToSchedule(group.groupeId, filiere.filiereId, niveau.niveau);
                                          }}
                                          className="p-1 text-primary-600 hover:bg-primary-100 rounded"
                                          title="Planifier"
                                        >
                                          <ArrowRight className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                  {niveau.groups.length === 0 && (
                                    <p className="pl-20 py-3 text-xs text-gray-500">Aucun groupe</p>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}

                  {filteredFilieres.length === 0 && (
                    <div className="px-4 py-8 text-center text-gray-500">
                      Aucune filière trouvée
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Detail Panel */}
            {selectedGroupeId && (
              <div className="w-96 bg-white border-l border-gray-200 flex flex-col overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">Détail des matières</h3>
                  <button
                    onClick={() => setSelectedGroupeId(null)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                {loadingGroupDetails ? (
                  <div className="flex-1 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                  </div>
                ) : (
                  <div className="flex-1 overflow-auto">
                    {groupModuleProgress.map((module) => (
                      <div
                        key={module.moduleElementId}
                        className="px-4 py-3 border-b border-gray-100 hover:bg-gray-50"
                      >
                        <div className="flex items-start gap-3">
                          {getStatusIcon(module.status, 'sm')}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 leading-snug line-clamp-2" title={module.moduleName}>
                              {module.moduleName}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500">
                                {Math.round(module.hoursScheduled * 10) / 10}h / {module.volumeHoraire}h
                              </span>
                            </div>
                            <div className="w-full h-1.5 bg-gray-200 rounded-full mt-2 overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all duration-300 ${getProgressBarColor(module.status)}`}
                                style={{ width: `${module.percentage}%` }}
                              />
                            </div>
                          </div>
                          <span className="text-xs text-gray-500">{Math.round(module.percentage)}%</span>
                        </div>
                      </div>
                    ))}

                    {groupModuleProgress.length === 0 && (
                      <div className="px-4 py-8 text-center">
                        <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Aucune matière configurée</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center gap-2 text-red-700 shadow-lg">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">{error}</span>
        </div>
      )}
    </div>
  );
};

export default SchedulingProgressPage;
